/**
 * Weekly 1:1 KPI scorecard fields — Airtable Account Manager weekly review.
 */
import { Field, Input, Textarea } from '../../../shell/Field.js';
import {
  WEEKLY_KPI_TARGETS,
  buildWeeklyScorecard,
  type WeeklyKpiInput,
  type WeeklyScorecard,
} from '@shared/util/weekly-scorecard.js';
import { inclusiveDayCount } from '@shared/util/dates.js';

export type WeeklyScorecardDraft = {
  periodStart: string;
  periodEnd: string;
  totalDials: string;
  liveConnects: string;
  qualifiedConversations: string;
  newSupportersAdded: string;
  devicesReceived: string;
  closeRate: string;
  avgDevicesPerSupporter: string;
  crmCompliance: string;
  wins: string;
  blockers: string;
  actions: string;
};

export const emptyWeeklyDraft = (): WeeklyScorecardDraft => ({
  periodStart: '',
  periodEnd: '',
  totalDials: '',
  liveConnects: '',
  qualifiedConversations: '',
  newSupportersAdded: '',
  devicesReceived: '',
  closeRate: '',
  avgDevicesPerSupporter: '',
  crmCompliance: '100',
  wins: '',
  blockers: '',
  actions: '',
});

export function weeklyDraftFromScorecard(s: WeeklyScorecard): WeeklyScorecardDraft {
  return {
    periodStart: s.periodStart.slice(0, 10),
    periodEnd: s.periodEnd.slice(0, 10),
    totalDials: String(s.totalDials),
    liveConnects: String(s.liveConnects),
    qualifiedConversations: String(s.qualifiedConversations),
    newSupportersAdded: String(s.newSupportersAdded),
    devicesReceived: String(s.devicesReceived),
    closeRate: s.closeRate === null ? '' : String(s.closeRate),
    avgDevicesPerSupporter: String(s.avgDevicesPerSupporter),
    crmCompliance: String(s.crmCompliance),
    wins: s.wins,
    blockers: s.blockers,
    actions: s.actions,
  };
}

function num(v: string): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function draftToWeeklyInput(draft: WeeklyScorecardDraft): WeeklyKpiInput {
  return {
    periodStart: draft.periodStart,
    periodEnd: draft.periodEnd,
    totalDials: num(draft.totalDials),
    liveConnects: num(draft.liveConnects),
    qualifiedConversations: num(draft.qualifiedConversations),
    newSupportersAdded: num(draft.newSupportersAdded),
    devicesReceived: num(draft.devicesReceived),
    closeRate: draft.closeRate.trim() === '' ? null : num(draft.closeRate),
    avgDevicesPerSupporter: num(draft.avgDevicesPerSupporter),
    crmCompliance: num(draft.crmCompliance),
    wins: draft.wins,
    blockers: draft.blockers,
    actions: draft.actions,
  };
}

export function draftToWeeklyScorecard(draft: WeeklyScorecardDraft): WeeklyScorecard {
  return buildWeeklyScorecard(draftToWeeklyInput(draft));
}

export function WeeklyScorecardFields({
  value,
  onChange,
}: {
  value: WeeklyScorecardDraft;
  onChange: (next: WeeklyScorecardDraft) => void;
}) {
  const set = (patch: Partial<WeeklyScorecardDraft>) => onChange({ ...value, ...patch });
  const preview = value.periodStart && value.periodEnd ? draftToWeeklyScorecard(value) : null;
  const days =
    value.periodStart && value.periodEnd
      ? Math.max(1, inclusiveDayCount(value.periodStart, value.periodEnd))
      : 0;
  const qualifiedTarget = days * WEEKLY_KPI_TARGETS.qualifiedConversationsPerDay;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Week start">
          <Input
            type="date"
            value={value.periodStart}
            onChange={(e) => set({ periodStart: e.target.value })}
          />
        </Field>
        <Field label="Week end">
          <Input
            type="date"
            value={value.periodEnd}
            onChange={(e) => set({ periodEnd: e.target.value })}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label={`Total dials (target ${WEEKLY_KPI_TARGETS.totalDials})`}>
          <Input type="number" min={0} step="1" value={value.totalDials} onChange={(e) => set({ totalDials: e.target.value })} />
        </Field>
        <Field label={`Live connects (${WEEKLY_KPI_TARGETS.liveConnectsMin}-${WEEKLY_KPI_TARGETS.liveConnectsMax})`}>
          <Input type="number" min={0} step="1" value={value.liveConnects} onChange={(e) => set({ liveConnects: e.target.value })} />
        </Field>
        <Field label={`Qualified conversations (${WEEKLY_KPI_TARGETS.qualifiedConversationsPerDay}x days${days ? ` = ${qualifiedTarget}` : ''})`}>
          <Input type="number" min={0} step="1" value={value.qualifiedConversations} onChange={(e) => set({ qualifiedConversations: e.target.value })} />
        </Field>
        <Field label={`New supporters (target ${WEEKLY_KPI_TARGETS.newSupportersAdded})`}>
          <Input type="number" min={0} step="1" value={value.newSupportersAdded} onChange={(e) => set({ newSupportersAdded: e.target.value })} />
        </Field>
        <Field label={`Devices received (target ${WEEKLY_KPI_TARGETS.devicesReceived})`}>
          <Input type="number" min={0} step="1" value={value.devicesReceived} onChange={(e) => set({ devicesReceived: e.target.value })} />
        </Field>
        <Field label="Close rate % (blank = N/A)">
          <Input type="number" min={0} max={100} step="0.1" value={value.closeRate} onChange={(e) => set({ closeRate: e.target.value })} placeholder="Optional" />
        </Field>
        <Field label={`Avg devices/supporter (target ${WEEKLY_KPI_TARGETS.avgDevicesPerSupporter})`}>
          <Input type="number" min={0} step="0.1" value={value.avgDevicesPerSupporter} onChange={(e) => set({ avgDevicesPerSupporter: e.target.value })} />
        </Field>
        <Field label="CRM compliance % (target 100)">
          <Input type="number" min={0} max={100} step="0.1" value={value.crmCompliance} onChange={(e) => set({ crmCompliance: e.target.value })} />
        </Field>
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 dark:border-sky-900 dark:bg-sky-950/40">
          <p className="text-[11px] font-medium uppercase tracking-wide text-sky-700 dark:text-sky-300">
            Overall performance score
          </p>
          <p className="mt-1 text-2xl font-semibold text-sky-900 dark:text-sky-100">
            {preview ? preview.overallPerformanceScore : '—'}
          </p>
          <p className="mt-0.5 text-[11px] text-sky-700/80 dark:text-sky-300/80">
            Avg of KPI attainment (close rate excluded)
          </p>
        </div>
      </div>

      <Textarea label="Wins" value={value.wins} onChange={(e) => set({ wins: e.target.value })} rows={2} placeholder="What went well this week?" />
      <Textarea label="Blockers" value={value.blockers} onChange={(e) => set({ blockers: e.target.value })} rows={2} placeholder="What is getting in the way?" />
      <Textarea label="Actions" value={value.actions} onChange={(e) => set({ actions: e.target.value })} rows={2} placeholder="Commitments for next week" />
    </div>
  );
}

export function WeeklyScorecardReadonly({ scorecard }: { scorecard: WeeklyScorecard }) {
  const items: Array<[string, string]> = [
    ['Period', `${scorecard.periodStart} → ${scorecard.periodEnd}`],
    ['Overall score', String(scorecard.overallPerformanceScore)],
    ['Total dials', `${scorecard.totalDials} / ${WEEKLY_KPI_TARGETS.totalDials}`],
    ['Live connects', `${scorecard.liveConnects} (target ${WEEKLY_KPI_TARGETS.liveConnectsMin}-${WEEKLY_KPI_TARGETS.liveConnectsMax})`],
    ['Qualified conversations', String(scorecard.qualifiedConversations)],
    ['New supporters', `${scorecard.newSupportersAdded} / ${WEEKLY_KPI_TARGETS.newSupportersAdded}`],
    ['Devices received', `${scorecard.devicesReceived} / ${WEEKLY_KPI_TARGETS.devicesReceived}`],
    ['Close rate', scorecard.closeRate === null ? 'N/A' : `${scorecard.closeRate}%`],
    ['Avg devices/supporter', `${scorecard.avgDevicesPerSupporter} / ${WEEKLY_KPI_TARGETS.avgDevicesPerSupporter}`],
    ['CRM compliance', `${scorecard.crmCompliance}%`],
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(([label, val]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{val}</p>
          </div>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Note title="Wins" body={scorecard.wins} />
        <Note title="Blockers" body={scorecard.blockers} />
        <Note title="Actions" body={scorecard.actions} />
      </div>
    </div>
  );
}

function Note({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      {body ? (
        <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700 dark:bg-slate-800/50 dark:text-slate-300">{body}</p>
      ) : (
        <p className="text-sm text-slate-400">None noted.</p>
      )}
    </div>
  );
}
